import { defineComponent as _defineComponent } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router/composables'
import { sortBy } from 'lodash'
import { useHubs } from '@/common/useHubs'
import VsButton from '@/components/vision/VsButton.vue'
import VsText from '@/components/vision/VsText.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import { useScroll } from '@vueuse/core'


export default /*#__PURE__*/_defineComponent({
  __name: 'NewJobButton',
  props: {
    parentRouteShortName: null,
    extraNavParams: { default: null },
    fullWidth: { type: Boolean },
    label: { default: 'New job' },
    defaults: { default: undefined },
    limitHubsTo: { default: () => [] },
    showManageClients: { type: Boolean, default: true }
  },
  setup(__props: any) {

const props = __props as { parentRouteShortName: string, extraNavParams: object | null, fullWidth?: boolean, label: string, defaults: {
      locationId: string | number | undefined
    }, limitHubsTo: string[], showManageClients: boolean }


const store = useStore()
const router = useRouter()

const searchBar = ref()
const showMenu = ref(false)
const searchTerm = ref('')
watch(showMenu, (val) => {
  if (!val) {
    searchTerm.value = ''
  } else {
    /*
      + Focusing on an <vs-text-input> (or any component) inside a v-menu is challenging because the menu tends to
      focus on the activator (v-btn) when it opens, which can interfere with an immediate input.focus() call.
      To handle this, a simple setTimeout() before focus is enough.

      + A note: using nextTick() instead of setTimeout() won't work here because the menu itself takes a bit longer
      to finish animating and displaying before it’s ready to accept focus on internal elements.

      + For my computer, 5ms is enough, but I set 100ms to compensate for slow computers,
      and also v-menu transition time is 300ms anyway.

      + This is unfortunately a downside of using pre-built component instead of our own.
     */
    setTimeout(() => {
      searchBar.value.$refs.input.focus()
    }, 100)
  }
})

const hubsList = ref()
const { y } = useScroll(hubsList)

const headerClass = computed(() => ({
  'header-shadow': y.value !== 0,
  'header-margin': true,
}))

const { accessibleHubs } = useHubs()

const limitedHubs = computed(() => {
  if (props.limitHubsTo.length > 0) {
    return (
      accessibleHubs.value?.filter((h) => props.limitHubsTo.includes(h.id)) ??
      []
    )
  } else {
    return accessibleHubs.value ?? []
  }
})

const hubsArray = computed(() =>
  sortBy(limitedHubs.value, (h) => h?.name.toLowerCase()).filter((h) =>
    h?.name.toLowerCase().includes(searchTerm.value.toLowerCase())
  )
)

const defaultJobValues = computed(() => {
  if (props.defaults?.locationId) {
    return {
      locationId: `${props.defaults.locationId}`,
    }
  }
  return undefined
})

const openNewJobSideSheet = (hubId: string) => {
  showMenu.value = false
  router.push({
    name: `${props.parentRouteShortName}-ticket-info-panel`,
    params: {
      id: 'new',
      hubId,
      ...props.extraNavParams,
    },
    query: defaultJobValues.value,
  })
}

const openCreateNewClient = () => {
  showMenu.value = false
  router.push({
    name: `${props.parentRouteShortName}-search-clients`,
  })
}

const showManageClientsButton = computed<boolean>(
  () => store.getters['permissions/isOrgAdmin'] && props.showManageClients
)

const clientButtonText = computed<string>(() =>
  hubsArray.value.length > 0 ? 'Manage clients' : 'Add clients'
)
const clientButtonIcon = computed<string>(() =>
  hubsArray.value.length > 0 ? 'settings' : 'add'
)

return { __sfc: true,props, store, router, searchBar, showMenu, searchTerm, hubsList, y, headerClass, accessibleHubs, limitedHubs, hubsArray, defaultJobValues, openNewJobSideSheet, openCreateNewClient, showManageClientsButton, clientButtonText, clientButtonIcon, VsButton, VsText, VsTextInput }
}

})